import { injectable, inject } from 'inversify';
import { GetProductCategoriesUseCase } from '../../../domain/usecases/ProductUseCases';
import type { ProductCategoryRepository } from '../../../domain/repositories';
import { ProductCategory } from '../../../domain/models';
import { TYPES } from '../../../di/types';

/**
 * POS categories — counts are **catalog-wide** (active products in category).
 * Counts come from `/api/categories` in one request (backend grouped aggregation).
 * Catalog is global across branches; stock stays branch-scoped on product cards.
 */
@injectable()
export class GetProductCategoriesUseCaseImpl implements GetProductCategoriesUseCase {
  constructor(
    @inject(TYPES.ProductCategoryRepository) private readonly categoryRepository: ProductCategoryRepository,
  ) {}

  async execute(branchId: string): Promise<ProductCategory[]> {
    try {
      this.validateBranchId(branchId);

      const dbCategories = await this.categoryRepository.findAll();
      const realCategories = dbCategories.filter(
        (cat) => cat.name.trim().toLowerCase() !== 'general',
      );

      return realCategories.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('❌ Get product categories failed:', error);
      throw new Error(
        `Failed to get product categories for branch ${branchId}: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  private validateBranchId(branchId: string): void {
    if (!branchId || branchId.trim().length === 0) {
      throw new Error('Branch ID is required');
    }
  }
}
